import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WatermarkedMedia from "../components/WatermarkedMedia";
import { api } from "../lib/api";
import { ArrowLeft, Calendar, Tag } from "lucide-react";

export function BlogIndex() {
  const [blogs, setBlogs] = useState([]);
  const [content, setContent] = useState({});
  useEffect(() => {
    Promise.all([api.getBlogs(), api.getContent()])
      .then(([b, c]) => {
        setBlogs(b || []);
        setContent(c || {});
      })
      .catch((err) => console.error("Failed to load blog index:", err));
  }, []);

  return (
    <div className="min-h-screen bg-onyx-900 text-ivory" data-testid="blog-index-page">
      <Navbar />
      <div className="pt-32 pb-16 text-center px-6">
        <span className="font-cinzel text-[11px] tracking-[0.4em] uppercase text-gold">
          The Journal
        </span>
        <h1 className="mt-4 font-bebas text-5xl md:text-7xl text-ivory">
          Stories From The <span className="text-gold-gradient">Set</span>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-ivory/65">
          Behind-the-scenes, industry essays and production playbooks from Mohali to
          Mumbai to London.
        </p>
      </div>
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
          {blogs.map((b, i) => (
            <Link
              key={b.id}
              to={`/blog/${b.slug}`}
              className="group gold-border bg-onyx-800 hover-lift overflow-hidden flex flex-col"
              data-testid={`blog-list-card-${i}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <WatermarkedMedia
                  src={b.cover_url}
                  alt={b.title}
                  position="bottom-right"
                  opacity={0.8}
                  watermarkSize={36}
                  showCaption={false}
                  className="h-full w-full transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex-1">
                <div className="flex flex-wrap gap-2 mb-3">
                  {(b.tags || []).slice(0, 3).map((t) => (
                    <span
                      key={t}
                      className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold border border-gold/30 px-2 py-0.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <h3 className="font-bebas text-2xl text-ivory group-hover:text-gold leading-tight">
                  {b.title}
                </h3>
                <p className="mt-3 text-ivory/60 text-sm line-clamp-3">{b.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
        {blogs.length === 0 && (
          <p className="text-center text-ivory/50 py-20">No stories published yet. Check back soon.</p>
        )}
      </div>
      <Footer contact={content.contact || {}} settings={content.settings || {}} />
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [content, setContent] = useState({});
  const [error, setError] = useState(false);

  useEffect(() => {
    api.getBlog(slug).then(setBlog).catch((err) => {
      console.error("Failed to load blog:", err);
      setError(true);
    });
    api.getContent().then(setContent).catch((err) => {
      console.error("Failed to load site content:", err);
    });
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-onyx-900 text-ivory">
        <div className="text-center">
          <p className="font-bebas text-4xl">Story not found.</p>
          <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-gold">
            <ArrowLeft size={16} /> Back to Journal
          </Link>
        </div>
      </div>
    );
  }
  if (!blog) {
    return <div className="min-h-screen bg-onyx-900" />;
  }

  return (
    <div className="min-h-screen bg-onyx-900 text-ivory" data-testid="blog-detail-page">
      <Navbar />
      <article className="pt-32 pb-20">
        <div className="mx-auto max-w-3xl px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gold hover:text-gold-bright text-xs font-cinzel tracking-[0.3em] uppercase mb-8"
          >
            <ArrowLeft size={14} /> All Stories
          </Link>
          <div className="flex flex-wrap gap-2 mb-5">
            {(blog.tags || []).map((t) => (
              <span
                key={t}
                className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold border border-gold/30 px-2 py-0.5"
              >
                {t}
              </span>
            ))}
          </div>
          <h1 className="font-bebas text-4xl md:text-6xl leading-tight" data-testid="blog-detail-title">
            {blog.title}
          </h1>
          <div className="mt-4 flex flex-wrap gap-5 text-xs text-ivory/55 font-cinzel tracking-[0.2em] uppercase">
            <span className="flex items-center gap-2"><Calendar size={12} />{new Date(blog.created_at).toLocaleDateString()}</span>
            <span>{blog.author}</span>
          </div>
          {blog.cover_url && (
            <div className="mt-10 aspect-[16/9] overflow-hidden gold-border">
              <WatermarkedMedia
                src={blog.cover_url}
                alt={blog.title}
                position="bottom-right"
                opacity={0.85}
                watermarkSize={56}
                showCaption={false}
                className="h-full w-full"
              />
            </div>
          )}
          <div className="mt-12 prose prose-invert max-w-none text-ivory/85 text-lg leading-[1.8] whitespace-pre-wrap" data-testid="blog-detail-body">
            {blog.body}
          </div>
        </div>
      </article>
      <Footer contact={content.contact || {}} settings={content.settings || {}} />
    </div>
  );
}
